import { useEffect } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { dsClass } from '../../common/constants/dsClasses.js';
import { PhoneNumberType } from '../../common/enums.js';
import { fieldCantBeEmptyMsg, invalidValueMsg, phoneNumberMsg, showMoreMsg, t } from '../../common/i18n/index.js';
import { DatePicker } from '../../common/react-hook-form/components/DatePicker.js';
import { SelectRadio } from '../../common/react-hook-form/components/SelectRadio.js';
import { PhoneNumber } from '../../common/react-hook-form/fields/PhoneNumber.js';
import { formatTimestampToDDMMYYYY } from '../../common/utils/dateUtils.js';
import { PHONE_NUMBER_REGEX } from '../../common/utils/phoneNumberUtils.js';
import { ErrorTooltip } from '../ErrorTooltip/ErrorTooltip.js';
import { Loading } from '../Loading/index.js';
import { NumberOwnerRHF } from '../NumberOwnerRHF/NumberOwnerRHF.js';
import { SelectNumberList, isNumberDisabled } from '../SelectNumberList/index.js';
import type { PhoneNumberOwner, SelectedPhoneNumber } from '../../common/types/subscription.js';
import type { ValidatedPhoneNumber } from '../../common/utils/phoneNumberUtils.js';

import './SelectPhoneNumber.scss';

export interface SelectPhoneNumberProps {
  holidays: Date[];
  cartItemInstanceId: string;
  companyName: string;
  datePickerLabel?: string;
  datePickerLabelOptionalText?: string;
  disabledPhoneNumbers: string[];
  existingPhoneNumberLabel?: string;
  getNextAllowedDay: (blockedDates: Date[]) => Date;
  isAllowedTransferDay: (dateToCheck: Date, blockedDates: Date[]) => boolean;
  onSelectUseExistingPhoneNumber: (existingPhoneNumber: string) => void;
  phoneNumbers: string[];
  phoneNumbersLoading: boolean;
  personOwnerEmailLabel?: string;
  phoneNumberOwner?: PhoneNumberOwner;
  selectedPhoneNumber?: SelectedPhoneNumber;
  validatedPhoneNumbers: ValidatedPhoneNumber[];
  initiallySelectedPhoneNumber?: string;
  onLoadMore: () => void;
}

export const SelectPhoneNumber = ({
  holidays,
  cartItemInstanceId,
  companyName,
  datePickerLabel = '',
  datePickerLabelOptionalText,
  disabledPhoneNumbers,
  existingPhoneNumberLabel = '',
  getNextAllowedDay,
  onSelectUseExistingPhoneNumber,
  phoneNumbers,
  phoneNumbersLoading,
  personOwnerEmailLabel = '',
  phoneNumberOwner,
  selectedPhoneNumber,
  validatedPhoneNumbers,
  initiallySelectedPhoneNumber,
  onLoadMore,
}: SelectPhoneNumberProps) => {
  const firstAllowedDay = getNextAllowedDay(holidays);

  const { control, watch, setValue } = useFormContext();
  const phoneNumberType = watch(`configuration.${cartItemInstanceId}.selectPhoneNumber.type`);

  // TODO this breaks validation if dispatched api call takes long time, it won't prevent submit
  const existingPhoneNumber = useWatch({
    name: `configuration.${cartItemInstanceId}.selectPhoneNumber.existingPhoneNumber`,
  });
  useEffect(() => {
    if (existingPhoneNumber) {
      onSelectUseExistingPhoneNumber(existingPhoneNumber);
    }
  }, [onSelectUseExistingPhoneNumber, existingPhoneNumber]);

  const getPhoneNumberValidationError = (phoneNumber: string) => {
    if (!phoneNumber) {
      return t.VPVR(fieldCantBeEmptyMsg);
    } else if (!PHONE_NUMBER_REGEX.test(phoneNumber)) {
      return t.DUPA(invalidValueMsg);
    }
    return validatedPhoneNumbers.find(it => it.phoneNumber === phoneNumber)?.error;
  };

  const newPhoneNumber = watch(`configuration.${cartItemInstanceId}.selectPhoneNumber.newPhoneNumber`);
  useEffect(() => {
    if (phoneNumberType === PhoneNumberType.NEW && newPhoneNumber === undefined) {
      // If we have a pre-assigned initial number, use it directly (avoids race condition
      // where disabledPhoneNumbers is stale due to 300ms debounce sync to parent form)
      if (initiallySelectedPhoneNumber && !isNumberDisabled(initiallySelectedPhoneNumber, disabledPhoneNumbers)) {
        setValue(`configuration.${cartItemInstanceId}.selectPhoneNumber.newPhoneNumber`, initiallySelectedPhoneNumber);
      } else if (initiallySelectedPhoneNumber === undefined) {
        // No initial number assigned (more subscriptions than pre-fetched numbers).
        // Don't auto-select from the pool — disabledPhoneNumbers may be stale, which
        // would cause duplicate selections. User will see the number list and can pick one.
      } else {
        // initiallySelectedPhoneNumber was provided but is disabled — find first available
        const autoSelectableNumber =
          phoneNumbers && phoneNumbers.find(phoneNumber => !isNumberDisabled(phoneNumber, disabledPhoneNumbers));
        if (autoSelectableNumber) {
          setValue(`configuration.${cartItemInstanceId}.selectPhoneNumber.newPhoneNumber`, autoSelectableNumber);
        }
      }
    }
  }, [
    cartItemInstanceId,
    phoneNumberType,
    newPhoneNumber,
    phoneNumbers,
    disabledPhoneNumbers,
    setValue,
    initiallySelectedPhoneNumber,
  ]);

  return (
    <div id={`select-phone-number-${cartItemInstanceId}`} className="of-select-phone-number">
      <h4 className={`${dsClass.MARGIN_TOP_1} ${dsClass.MARGIN_BOTTOM_3}`}>{t.AGIJ(phoneNumberMsg)}</h4>
      <SelectRadio
        name={`configuration.${cartItemInstanceId}.selectPhoneNumber.type`}
        defaultValue={selectedPhoneNumber?.type || PhoneNumberType.EXISTING}
        items={[
          {
            label: t.VPJ3('Transfer current phone number'),
            value: PhoneNumberType.EXISTING,
          },
          {
            label: t.ARFV('New number'),
            value: PhoneNumberType.NEW,
          },
        ]}
      />
      {phoneNumberType === PhoneNumberType.EXISTING && (
        <>
          <div className="of-select-phone-number__existing">
            <PhoneNumber
              name={`configuration.${cartItemInstanceId}.selectPhoneNumber.existingPhoneNumber`}
              type="text"
              label={existingPhoneNumberLabel}
              placeholder={t.AGIJ(phoneNumberMsg)}
              disabled={false}
              required={true}
              validate={getPhoneNumberValidationError}
            />
          </div>
          <NumberOwnerRHF
            {...{
              companyName,
              cartItemInstanceId: cartItemInstanceId,
              personOwnerEmailLabel,
              phoneNumberOwner,
            }}
          />
          <h4 className={`${dsClass.MARGIN_TOP_6} ${dsClass.MARGIN_BOTTOM_3}`}>
            {t.BRWO('Select your preferred time of transfer')}
          </h4>
          <div className="of-select-phone-number__transfer-date-input">
            <DatePicker
              name={`configuration.${cartItemInstanceId}.selectPhoneNumber.transferDate`}
              defaultValue={
                selectedPhoneNumber?.transferDate
                  ? formatTimestampToDDMMYYYY(selectedPhoneNumber.transferDate.getTime())
                  : formatTimestampToDDMMYYYY(getNextAllowedDay(holidays).getTime())
              }
              disableWeekends={true}
              blockedDates={holidays}
              label={datePickerLabel}
              optionalText={datePickerLabelOptionalText}
              visibleRange={[
                [firstAllowedDay.getFullYear(), firstAllowedDay.getMonth() + 1, firstAllowedDay.getDate()],
                [9999, 12, 31],
              ]}
            />
          </div>
        </>
      )}
      {phoneNumberType === PhoneNumberType.NEW && (
        <>
          <Controller
            name={`configuration.${cartItemInstanceId}.selectPhoneNumber.newPhoneNumber`}
            control={control}
            shouldUnregister={true}
            rules={{ required: { value: true, message: t.VPVR(fieldCantBeEmptyMsg) } }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <>
                {error && <ErrorTooltip placement="top">{error?.message}</ErrorTooltip>}
                <SelectNumberList
                  disabledNumbers={disabledPhoneNumbers}
                  getNewNumbersLinkText={t.H6OA(showMoreMsg)}
                  inset
                  numbers={
                    // Ensure the selected number is always visible in the grid,
                    // even if it's beyond the currently loaded pool
                    value && !phoneNumbers.includes(value) ? [value, ...phoneNumbers] : phoneNumbers
                  }
                  onSelectNumber={(selectedNumber: string) => {
                    onChange(selectedNumber);
                  }}
                  selectedNumber={value}
                  showMoreNumbersLink={phoneNumbers.length < 30 && !phoneNumbersLoading}
                  onLoadMore={onLoadMore}
                />
              </>
            )}
          />
          {phoneNumbersLoading && <Loading />}
        </>
      )}
    </div>
  );
};
